import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { RoadmapUseCaseDetail } from "../../api/roadmapClient";
import {
  fetchRoadmapUseCaseConversation,
  sendRoadmapUseCaseConversationMessage,
  validateRoadmapUseCaseIdea,
} from "../../api/roadmapClient";
import type { RoadmapUseCaseConversationMessage } from "../../types/roadmapConversations";

interface UseCaseConversationPanelProps {
  useCaseId: string;
  onUseCaseUpdated?: (useCase: RoadmapUseCaseDetail) => void;
}

export function UseCaseConversationPanel({
  useCaseId,
  onUseCaseUpdated,
}: UseCaseConversationPanelProps): JSX.Element {
  const [messages, setMessages] = useState<RoadmapUseCaseConversationMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessages([]);
    setInput("");
    setError(null);
    void loadConversation();
  }, [useCaseId]);

  const orderedMessages = useMemo(() => {
    return [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [messages]);

  const latestAssistantMessage = useMemo(() => {
    return [...orderedMessages].reverse().find((message) => message.role === "assistant") ?? null;
  }, [orderedMessages]);

  const validationCandidate = latestAssistantMessage?.content?.trim() || input.trim();

  const loadConversation = async () => {
    if (!useCaseId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchRoadmapUseCaseConversation(useCaseId);
      setMessages(result.messages);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load conversation";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!useCaseId || !input.trim()) return;

    setIsSending(true);
    setError(null);

    try {
      const result = await sendRoadmapUseCaseConversationMessage(useCaseId, input.trim());
      setMessages(result.messages.length ? result.messages : [result.message]);
      setInput("");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send message";
      // eslint-disable-next-line no-console
      console.error("Use case conversation send failed", err);
      setError(message);
    } finally {
      setIsSending(false);
    }
  };

  const handleValidateIdea = async () => {
    if (!useCaseId || !validationCandidate) return;
    setIsValidating(true);
    setError(null);
    setNotice(null);

    try {
      const updated = await validateRoadmapUseCaseIdea(useCaseId, validationCandidate);
      setNotice("Idée validée et enregistrée dans le use case.");
      onUseCaseUpdated?.(updated);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to validate idea";
      // eslint-disable-next-line no-console
      console.error("Use case validation failed", err);
      setError(message);
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    if (!messageListRef.current) return;
    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  }, [orderedMessages]);

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-border bg-card shadow-soft">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div>
            <div className="text-sm font-semibold text-foreground">Assistant review</div>
            <div className="text-xs text-muted-foreground">
              Challenge l'idée, propose des améliorations et des contre-propositions.
            </div>
          </div>
          <button type="button" className="button button-ghost button-xs" onClick={() => void loadConversation()}>
            {isLoading ? "Chargement…" : "Rafraîchir"}
          </button>
        </div>

        <div ref={messageListRef} className="max-h-64 space-y-3 overflow-y-auto px-3 py-3">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Chargement de la conversation…</p>
          ) : orderedMessages.length === 0 ? (
            <p className="text-sm text-muted-foreground">Démarrez la discussion pour revoir l'idée.</p>
          ) : (
            orderedMessages.map((message) => (
              <div
                key={message.id}
                className={`flex flex-col gap-1 rounded border px-3 py-2 text-sm ${
                  message.role === "assistant"
                    ? "border-indigo-100 bg-indigo-50/60 text-foreground"
                    : "border-border bg-white text-foreground"
                }`}
              >
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
                  <span>{message.role}</span>
                  <span>{new Date(message.createdAt).toLocaleString()}</span>
                </div>
                <div className="whitespace-pre-line">{message.content}</div>
              </div>
            ))
          )}
        </div>

        <form className="border-t border-border bg-muted/40 px-3 py-3" onSubmit={handleSend}>
          <label className="flex flex-col gap-2 text-xs text-muted-foreground">
            Message
            <textarea
              className="input min-h-[90px]"
              placeholder="Décrivez l'idée ou demandez un retour"
              value={input}
              onChange={(event: any) => setInput(event.target.value)}
              disabled={isSending}
            />
          </label>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="submit" className="button button-primary" disabled={isSending || !input.trim()}>
              {isSending ? "Envoi…" : "Envoyer"}
            </button>
            <button
              type="button"
              className="button button-secondary"
              disabled={isValidating || !validationCandidate}
              onClick={() => void handleValidateIdea()}
            >
              {isValidating ? "Validation…" : "Valider l’idée"}
            </button>
            <span className="text-xs text-muted-foreground">
              {latestAssistantMessage ? "Utilise la dernière réponse de l'assistant." : "Utilise votre message actuel."}
            </span>
          </div>
        </form>
      </div>

      {notice ? (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          {notice}
        </div>
      ) : null}
      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
      ) : null}
    </div>
  );
}
